"use client";

import type { ReactNode } from "react";

// Small building blocks for the Settings page (tabs, cards, "coming soon"
// markers). Anything the backend can't do yet is drawn disabled and tagged
// "Coming soon" so the layout is complete without pretending it works.

export function Icon({ children, className = "h-5 w-5" }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function SoonBadge() {
  return (
    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-orange">
      Coming soon
    </span>
  );
}

// A white panel with a round tinted icon, a title and a one-line description.
export function SettingsCard({
  icon,
  title,
  subtitle,
  soon = false,
  className = "",
  children,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  soon?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section className={`flex flex-col rounded-xl border border-border-light bg-bg-card p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-accent-blue">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold">{title}</h3>
            {soon && <SoonBadge />}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-text-secondary">{subtitle}</p>}
        </div>
      </div>
      {children !== undefined && (
        <div className={`mt-4 flex flex-1 flex-col gap-3 ${soon ? "opacity-60" : ""}`}>{children}</div>
      )}
    </section>
  );
}

export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div className="mt-auto flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-text-secondary">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent-blue">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </Icon>
      <span>{children}</span>
    </div>
  );
}

export function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed ${
        checked ? "bg-accent-green" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
          checked ? "left-[18px]" : "left-0.5"
        }`}
      />
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-primary">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-text-tertiary">{hint}</span>}
    </label>
  );
}

// A whole tab that isn't built yet: one tidy card per planned feature.
export function PlannedGrid({ items }: { items: { icon: ReactNode; title: string; text: string }[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => (
        <SettingsCard key={item.title} icon={item.icon} title={item.title} subtitle={item.text} soon />
      ))}
    </div>
  );
}
